import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
    FormControlLabel,
    Grid,
    Paper,
    Switch,
    Toolbar,
    styled,
    useTheme,
} from '@mui/material';

import { useAppDispatch, useAppSelector } from '../state/hooks';
import {
    fetchBottomBalanceAreas,
    fetchBottomDensityAreas,
    fetchBottomEmptyMilesAreas,
    fetchTopBalanceAreas,
    fetchTopDensityAreas,
} from '../state/features/areaHealthSlice';
import { ApiStatus } from '../utils/enums';
import Hidden from '../components/layout/hidden';
import AreaHealthTable from '../components/Tables/AreaHealthTable';
import AreaHealthMap from '../components/Map/AreaHealthMap';
import HealthToolbar from '../components/Toolbars/healthToolbar';

const classesPrefix = 'areaHealth';

const classes = {
    pageRoot: `${classesPrefix}-pageRoot`,
    pageRootItem: `${classesPrefix}-pageRootItem`,
    tableWrapper: `${classesPrefix}-tableWrapper`,
    mapToolbar: `${classesPrefix}-mapToolbar`,
};

const StyledGrid = styled(Grid)(({ theme }) => {
    return {
        [`&.${classes.pageRoot}`]: {
            minHeight: '100%',
        },
        [`& .${classes.pageRootItem}`]: {
            overflow: 'auto',
            margin: theme.spacing(2),
        },
        [`& .${classes.tableWrapper}`]: {
            height: 'calc(100vh - 144px)',
        },
        [`& .${classes.mapToolbar}`]: {
            backgroundColor: theme.palette.info.light,
            color: theme.palette.getContrastText(theme.palette.info.light),
        },
    };
});

const AreaHealth = (): React.JSX.Element => {
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const location = useLocation();

    const [isBalanceVisible, setIsBalanceVisible] = useState(location.state?.showBalance !== undefined ? location.state?.showBalance : true);
    const [isDensityVisible, setIsDensityVisible] = useState(location.state?.showDensity !== undefined ? location.state?.showDensity : false);
    const [isEmptyMilesVisible, setIsEmptyMilesVisible] = useState(location.state?.showEmptyMiles !== undefined ? location.state?.showEmptyMiles : false);

    const bottomBalanceAreasApiStatus = useAppSelector((state) => { return state.areaHealth.bottomBalanceAreasApiStatus; });
    const bottomBalanceAreas = useAppSelector((state) => { return state.areaHealth.bottomBalanceAreas; });
    const topBalanceAreasApiStatus = useAppSelector((state) => { return state.areaHealth.topBalanceAreasApiStatus; });
    const topBalanceAreas = useAppSelector((state) => { return state.areaHealth.topBalanceAreas; });

    const bottomDensityAreasApiStatus = useAppSelector((state) => { return state.areaHealth.bottomDensityAreasApiStatus; });
    const bottomDensityAreas = useAppSelector((state) => { return state.areaHealth.bottomDensityAreas; });
    const topDensityAreasApiStatus = useAppSelector((state) => { return state.areaHealth.topDensityAreasApiStatus; });
    const topDensityAreas = useAppSelector((state) => { return state.areaHealth.topDensityAreas; });

    const bottomEmptyMilesAreasApiStatus = useAppSelector((state) => { return state.areaHealth.bottomEmptyMilesAreasApiStatus; });
    const bottomEmptyMilesAreas = useAppSelector((state) => { return state.areaHealth.bottomEmptyMilesAreas; });

    useEffect(() => {
        dispatch(fetchBottomBalanceAreas());
    }, [dispatch]);

    useEffect(() => {
        dispatch(fetchTopBalanceAreas());
    }, [dispatch]);

    useEffect(() => {
        dispatch(fetchBottomDensityAreas());
    }, [dispatch]);

    useEffect(() => {
        dispatch(fetchTopDensityAreas());
    }, [dispatch]);

    useEffect(() => {
        dispatch(fetchBottomEmptyMilesAreas());
    }, [dispatch]);

    const handleBalanceChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setIsBalanceVisible(event.target.checked);
    };

    const handleDensityChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setIsDensityVisible(event.target.checked);
    };

    const handleEmptyMilesChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setIsEmptyMilesVisible(event.target.checked);
    };

    return (
        <StyledGrid
            container
            className={classes.pageRoot}
            justifyContent='flex-start'
            direction='column'
        >
            <Grid item xs className={classes.pageRootItem}>
                <Paper>
                    <HealthToolbar pageTitle='Area Health' />
                    <Grid container>
                        <Grid item xs={12} md={6} lg={4} className={classes.tableWrapper}>
                            <AreaHealthTable />
                        </Grid>
                        <Hidden direction='down' breakpoint='md'>
                            <Grid item xs={12} md={6} lg={8}>
                                <Toolbar variant='dense' className={classes.mapToolbar}>
                                    <Grid
                                        container
                                        sx={{
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        <Grid item>
                                            <FormControlLabel
                                                control={
                                                    <Switch color='warning' checked={isBalanceVisible} onChange={handleBalanceChange} />
                                                }
                                                label='Area Balance'
                                            />
                                        </Grid>
                                        <Grid item>
                                            <FormControlLabel
                                                control={
                                                    <Switch color='warning' checked={isDensityVisible} onChange={handleDensityChange} />
                                                }
                                                label='Density'
                                            />
                                        </Grid>
                                        <Grid item>
                                            <FormControlLabel
                                                control={
                                                    <Switch color='warning' checked={isEmptyMilesVisible} onChange={handleEmptyMilesChange} />
                                                }
                                                label='Empty Mile %'
                                            />
                                        </Grid>
                                    </Grid>
                                </Toolbar>

                                <AreaHealthMap
                                    isFetchingBalanceData={
                                        bottomBalanceAreasApiStatus === ApiStatus.Idle || bottomBalanceAreasApiStatus === ApiStatus.Loading ||
                                        topBalanceAreasApiStatus === ApiStatus.Idle || topBalanceAreasApiStatus === ApiStatus.Loading
                                    }
                                    showBalance={isBalanceVisible}
                                    balanceAreas={[...bottomBalanceAreas.areas, ...topBalanceAreas.areas]}
                                    isFetchingDensityData={
                                        bottomDensityAreasApiStatus === ApiStatus.Idle || bottomDensityAreasApiStatus === ApiStatus.Loading ||
                                        topDensityAreasApiStatus === ApiStatus.Idle || topDensityAreasApiStatus === ApiStatus.Loading
                                    }
                                    showDensity={isDensityVisible}
                                    densityAreas={[...bottomDensityAreas.areas, ...topDensityAreas.areas]}
                                    isFetchingEmptyMilesData={
                                        bottomEmptyMilesAreasApiStatus === ApiStatus.Idle || bottomEmptyMilesAreasApiStatus === ApiStatus.Loading
                                    }
                                    showEmptyMiles={isEmptyMilesVisible}
                                    emptyMilesAreas={bottomEmptyMilesAreas.areas}
                                    theme={theme}
                                />
                            </Grid>
                        </Hidden>
                    </Grid>
                </Paper>
            </Grid>
        </StyledGrid>
    );
};

export default AreaHealth;
